import { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { IconBell } from '@tabler/icons-react';

import { Breadcrumb } from '@/components/BreadCrumbs';
import NotificationDropdown from '@/components/NotificationDropdown';
import Select from '@/components/Select';
import { LANGUAGE_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getWebNotificationList } from '@/service/notificationServices';
import { updateSettings as updateSettingsApi } from '@/service/settingsServices';
import { useSettingsStore } from '@/store/useSettingsStore';

const Header = () => {
  const { breadcrumb, title } = usePageTitle();
  const settings = useSettingsStore((s) => s.settings);
  const updateStore = useSettingsStore((s) => s.updateSettings);
  const { formatText } = useFormatText();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const bellRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Sync current language to service worker for push notification translations
  useEffect(() => {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SET_LANGUAGE',
        language: settings.language,
      });
    }

    // Listen for the service worker asking for the current language (e.g. after SW restart)
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GET_LANGUAGE' && event.ports[0]) {
        event.ports[0].postMessage(settings.language);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [settings.language]);

  // Fetch unread notification count on mount (first login) and on every route change
  const fetchUnreadCount = useCallback(async () => {
    const response = await getWebNotificationList({ limit: 1, offset: 0 });
    if (response?.status) {
      setUnreadCount(response.unread_count || 0);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [location.pathname, fetchUnreadCount]);

  const handleLanguageChange = async (value: string | string[]) => {
    const langValue = Array.isArray(value) ? value[0] : (value as string);
    const newLang = langValue as 'en' | 'ar';

    updateStore({
      language: newLang,
      direction: newLang === 'ar' ? 'rtl' : 'ltr',
    });

    await updateSettingsApi({
      language: newLang,
      date_format: settings.dateFormat,
      currency: settings.currency,
    });
  };

  return (
    <header className="flex flex-wrap items-center justify-between bg-white gap-4">
      <div className="flex items-center gap-2">
        {/* Toggle button for Mobile and Desktop */}
        {/* <div className="md:hidden size-6 rotate-180" /> */}

        {breadcrumb.length > 0 ? (
          <Breadcrumb
            path={breadcrumb.map((item) => ({
              label: item.label,
              href: item.to || '',
              state: item.state,
            }))}
          />
        ) : (
          <p className="font-bold text-base leading-[150%] text-neutral-primary">
            {title}
          </p>
        )}
      </div>

      <div className="flex items-center gap-4">
        <Select
          placeholder={formatText('header.language')}
          options={LANGUAGE_OPTIONS}
          selected={settings.language}
          onChange={handleLanguageChange}
          className="w-37.5! py-2.25"
        />

        <div
          ref={bellRef}
          onClick={() => setIsNotificationOpen((prev) => !prev)}
          className="relative p-2 h-10 w-10 border border-neutral-light-200 shadow-small text-neutral-500 rounded-lg cursor-pointer hover:bg-neutral-50 transition"
        >
          <IconBell size={20} color="#717784" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-error-secondary text-white text-[10px] font-semibold leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>

        <NotificationDropdown
          isOpen={isNotificationOpen}
          onClose={() => setIsNotificationOpen(false)}
          anchorRef={bellRef}
          onUnreadCountChange={setUnreadCount}
        />
      </div>
    </header>
  );
};

export default Header;
